/**
 * Estado de una herramienta en la URL, para poder pasarle a otro lo que armaste.
 *
 * Por que existe: las 14 herramientas guardan lo que configuras, pero todas en
 * localStorage y ninguna en la URL. O sea que nada de lo que un jugador construye
 * aca se puede compartir: ni una configuracion de bestia, ni un plan de edificios.
 * Y los jugadores de War and Order viven en Discords de alianza, que es donde se
 * pasan todo. Cada enlace que circula es una visita nueva y un enlace entrante.
 *
 * QUE SE CODIFICA, Y QUE NO. Lo guardado por /beast/ ocupa 21,7 KB de JSON, pero
 * casi todo son datos del juego —niveles, puntos, textos— que ya viajan en el
 * bundle. Meterlos en la URL da 29 KB en base64, o 4,9 KB comprimido: en los dos
 * casos, de sobra para lo que aguantan los navegadores y para lo que Discord o
 * WhatsApp dejan pegar sin cortar.
 *
 * Por eso cada herramienta codifica solo lo que el jugador eligio y reconstruye el
 * resto desde sus propios datos al abrir el enlace. Para /beast/ eso son la bestia,
 * los talentos disponibles y 19 numeros: unos 200 caracteres.
 *
 * Efecto util de paso: un enlace no se rompe cuando cambian los datos del juego,
 * porque lo que lleva son las decisiones, no la tabla.
 */

/** Parametro donde viaja el estado. Corto porque va en una URL que se pega a mano. */
export const PARAM = 's'

const aBase64Url = texto =>
  btoa(String.fromCharCode(...new TextEncoder().encode(texto)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

const deBase64Url = texto => {
  const normal = texto.replace(/-/g, '+').replace(/_/g, '/')
  const binario = atob(normal + '='.repeat((4 - (normal.length % 4)) % 4))

  return new TextDecoder().decode(Uint8Array.from(binario, c => c.charCodeAt(0)))
}

/** Codifica un objeto pequeño a una cadena apta para una URL. */
export const codificar = minimo => {
  try {
    return aBase64Url(JSON.stringify(minimo))
  } catch {
    return null
  }
}

/** Inversa de `codificar`. Devuelve null si la cadena no es nuestra o esta cortada. */
export const decodificar = cadena => {
  if (!cadena || typeof cadena !== 'string') return null
  try {
    const obj = JSON.parse(deBase64Url(cadena))

    return obj && typeof obj === 'object' ? obj : null
  } catch {
    // Un enlace truncado al pegarlo es el caso frecuente. No es un error del sitio:
    // la herramienta abre con su estado normal y no se avisa de nada.
    return null
  }
}

/**
 * Lee el estado que venga en la URL actual. Solo en cliente — en el prerenderizado
 * no hay URL que leer, y el HTML estatico es el mismo para todos los enlaces.
 */
export const leerEstadoDeUrl = () => {
  if (typeof window === 'undefined') return null

  return decodificar(new URLSearchParams(window.location.search).get(PARAM))
}

/** Construye el enlace absoluto que se copia al portapapeles. */
export const construirEnlace = minimo => {
  const cadena = codificar(minimo)
  if (!cadena || typeof window === 'undefined') return null

  return `${window.location.origin}${window.location.pathname}?${PARAM}=${cadena}`
}
